import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { find } from 'lodash';
import {Col, Flex, Form, Input, Row, Table, Select, Radio, Button, Popconfirm, Switch } from "antd";
import { DeleteFilled } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { getSavedFilters, clearData, updateSavedFilter, deleteSavedFilter } from "./savedFiltersSlice";
import { buildQueryString, debounce } from "../../utils/helpers";
import { HeaderCell } from "./HeaderCell";
import { IconSearch } from "../../components/IconSearch/IconSearch";
import { IconShare } from "../../components/IconShare/IconShare";
import { IconDefault } from "../../components/IconDefault/IconDefault";
import { MainButton } from "../../components/MainButton/MainButton";
import { Notification } from "../../components/Notification/Notification";
import { IconEdit } from "../../components/IconEdit/IconEdit";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
//import SortableTable from "../../components/SortableTable/SortableTable";

import css from "./SavedFilters.module.css";

export const SavedFilters = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { savedFilters, getSavedFiltersInProgress } = useSelector((state) => state.savedFilters);

  const [type, setType] = useState('search');
  const [showNotification, setShowNotification] = useState(false);
  const [showNotificationMessage, setShowNotificationMessage] = useState('');
  

  useEffect(() => {
    dispatch(getSavedFilters());
    return () => {
      dispatch(clearData());
    };
  }, [dispatch]);

  const onUpdateSavedFilter = (id) => {
    const name = prompt('New Filter Name?')
    dispatch(updateSavedFilter({id, name})).then(()=>{
      setShowNotification(true);
      setShowNotificationMessage("Saved Filter was updated successfully.");
    })
  }
  
  const onToggleSavedFilter = (id, prop, value) => {
    dispatch(updateSavedFilter({id, [prop]: value})).then(()=>{
      setShowNotification(true);
      setShowNotificationMessage("Saved Filter was updated successfully.");
    })
  }
  
  const onDefaultSavedFilter = (id, prop, value) => {
    let calls = [
      dispatch(updateSavedFilter({id, [prop]: value}))
    ]
    const existingDefault = find(savedFilters, filter=>filter.default);
    if(existingDefault)
      calls.push(dispatch(updateSavedFilter({id: existingDefault.id, 'default': false})));    
      
    Promise.all(calls).then(()=>{
      setShowNotification(true);
      setShowNotificationMessage("Saved Filter was updated successfully.");
    })
  }
  
  const onDeleteSavedFilter = (id) => {
    dispatch(deleteSavedFilter(id)).then(()=>{
      setShowNotification(true);
      setShowNotificationMessage("Saved Filter was deleted successfully.");
    })
  }
  
  const columns = [
    {
      title: "Name",
      editable: false,
      dataIndex: "name",
      align: "left",
      sorter: (a, b) => a.name.localeCompare(b.name), // Define a sorter function
      defaultSortOrder: 'ascend', 
    },
    /*
    {
      title: "Type",
      editable: false,
      dataIndex: "type",
      align: "left",
    },
    */
    {
      title: "Shared",
      editable: false,
      dataIndex: "shared",
      align: "left",
      width: 70,
      render: (_, rowData) => <Flex gap={16} align={"center"}>
        <span className={css.switchContainer}>
          <Switch size="small" checked={rowData.shared} onChange={()=>onToggleSavedFilter(rowData.id, 'shared', !rowData.shared)} />
        </span>
      </Flex>
    },
    {
      title: "Default",
      editable: false,
      dataIndex: "default",
      align: "left",
      width: 70,
      render: (_, rowData) => <Flex gap={16} align={"center"}>
        <span className={css.switchContainer}>
          <Switch size="small" checked={rowData.default} onChange={()=>onDefaultSavedFilter(rowData.id, 'default', !rowData.default)} />
        </span>
      </Flex>
    },


    {
      title: "Actions",
      editable: false,
      dataIndex: "Actions",
      align: "left",
      width: 100,
      render: (_, rowData) => {
        return (
          <Flex gap={16} align={"center"}>
            <div className={css.actionContainer}>
              <span
                className={css.actionIcon}
                onClick={()=>onUpdateSavedFilter(rowData.id)}
              >
                <IconEdit />
              </span>
            </div>
            {/*
            <div className={css.actionContainer}>
              <span
                className={css.actionIcon}
                onClick={()=>onToggleSavedFilter(rowData.id, 'shared', !rowData.shared)}
              >
                <IconShare active={rowData.shared} />
              </span>
            </div>
            <div className={css.actionContainer}>
              <span
                className={css.actionIcon}
                onClick={()=>onDefaultSavedFilter(rowData.id, 'default', !rowData.default)}
              >
                <IconDefault active={rowData.default} />
              </span>
            </div>
            */}


            <div className={css.actionContainer}>
              <Popconfirm
                title="Are you sure you want to delete this saved filter?"
                onConfirm={() => {
                  onDeleteSavedFilter(rowData.id)
                }}
                okText="Yes"
                cancelText="No"
              >
                <span className={css.actionIcon}>
                <DeleteFilled />
                </span>
              </Popconfirm>
            </div>
          </Flex>
        );
      },
    },
  ];
  
  

  const handleCreateSavedFilterClick = () => {
    navigate("/admin/savedFilters/new");
  };
  
  const tableData = savedFilters.filter(f=>f.type == type);

  return (<>
    <Breadcrumbs 
      basePath="admin" 
      paths={[
        { path: '/savedFilters', breadcrumbName: 'SavedFilter Management' },
      ]}
    />
    <PageHeader title="User Preferences">
    </PageHeader> 
    <Notification
      message="Success"
      description={showNotificationMessage}
      showNotification={showNotification}
      cancel={() => setShowNotification(false)}
    />


    <Flex className={css.root} vertical>
      <div className={"ibox-holder"}>
        <Flex style={{ marginBottom: "16px" }}>
          <Radio.Group value={type} onChange={(e)=>setType(e.target.value)}>
            <Radio.Button value={'fields'}>Saved Fields</Radio.Button>
            <Radio.Button value={'search'}>Saved Filters</Radio.Button>
          </Radio.Group>
        </Flex>

        <Table
          components={{
            header: {
              row: HeaderCell,
            },
          }}
          columns={columns.filter(column=>(!(type == 'search' && column.dataIndex=='default')))}
          rowKey={(savedFilter) => savedFilter.id}
          dataSource={tableData}
          pagination={{
            position: ["bottomRight"],
            responsive: true,
            pageSize: 10,
            hideOnSinglePage: true,
            showSizeChanger: false,
          }}
          loading={getSavedFiltersInProgress}
          style={{ marginTop: 10 }}
          scroll={{ x: "100%" }}
        />
      </div>
    </Flex>
    </>
  );
};
